import { Skeleton } from '@mantine/core';
import { IconCheckbox } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import Button from 'components/Button';
import { useConfirmWithMessage } from 'components/modals/confirm-with-message/ConfirmWithMessageProvider';
import { PriceOfferBulkActionProps } from 'components/tables/data/price-offer/types';
import panic from 'errors/panic';
import { noop } from 'lodash';
import { useMemo, useState } from 'react';

/**
 * Performs a bulk close on all selected actionable price offers.
 */
export default function BulkCloseAction({ priceOffers, onBulkActionComplete = noop }: PriceOfferBulkActionProps) {
  const { getAction } = useApi();
  const { confirmWithMessage } = useConfirmWithMessage();
  const [loading, setLoading] = useState(false);

  const availablePriceOffers = useMemo(
    () => priceOffers.filter((priceOffer) => priceOffer.actionPermissions.canClose),
    [priceOffers]
  );

  /**
   * Evaluates all selected actionable price offers using one shared decision.
   */
  const closeAll = () =>
    confirmWithMessage({
      title: 'Vyhodnotiť cenové ponuky',
      message: `Vyberte vyhodnotenie pre ${availablePriceOffers.length} vybraných cenových ponúk.`,
      reasonLabel: 'Poznámka',
      selectLabel: 'Vyhodnotenie',
      selectPlaceholder: 'Vyberte vyhodnotenie',
      selectOptions: [
        { value: 'acceptable', label: 'Vyhovujúca' },
        { value: 'unacceptable', label: 'Nevyhovujúca' },
      ],
      requiredSelect: true,
      onConfirmWithMessage: async (note?: string, decision?: string) => {
        try {
          setLoading(true);
          const action = getAction('PriceOfferClose');

          for (const priceOffer of availablePriceOffers) {
            await action({
              parameters: { priceOfferId: String(priceOffer.priceOfferId) },
              payload: { decision: decision ?? 'acceptable', note },
            });
          }

          onBulkActionComplete(decision === 'unacceptable' ? 'unacceptable' : 'acceptable', availablePriceOffers);
        } catch (error) {
          panic(error as Error);
        } finally {
          setLoading(false);
        }
      },
    });

  if (loading) {
    return <Skeleton w={120} h={32} radius={4} />;
  }

  if (availablePriceOffers.length === 0) {
    return <></>;
  }

  return (
    <Button variant="primary" size="sm" leftIcon={<IconCheckbox />} onClick={closeAll}>
      Vyhodnotiť ({availablePriceOffers.length})
    </Button>
  );
}
